import { Row } from '@tanstack/table-core'
import FabricTable from 'components/productDetails/FabricTable'
import PillowTable from 'components/productDetails/PillowTable'
import PremadeTable from 'components/productDetails/PremadeTable'
import TableTable from 'components/productDetails/TableTable'
import TowelTable from 'components/productDetails/TowelTable'
import { AuditAction, AuditType } from 'types/audit'
import { DocumentType, PaymentMethod } from 'types/orders'
import { Category, Place, Unit } from 'types/products'
import { ShortageOrder, ShortageOrderTableItem, ShortageStatus } from 'types/shortage'
import { OrderPlace, SewingWorkType, WorkType } from 'types/work'

export function getCategoryName(
  category: Category,
  { capitalize = false, short = false }: { capitalize?: boolean; short?: boolean } = {}
) {
  let header: string
  let mianownik: string
  let biernik: string
  switch (category) {
    case Category.PREMADE:
      header = short ? 'Gotowe' : 'Produkty gotowe'
      mianownik = short ? 'gotowy' : 'produkt gotowy'
      biernik = short ? 'gotowy' : 'produkt gotowy'
      break
    case Category.FABRIC:
      header = short ? 'Metrażowe' : 'Produkty metrażowe'
      mianownik = short ? 'metrażowy' : 'produkt metrażowy'
      biernik = short ? 'metrażowy' : 'produkt metrażowy'
      break
    case Category.PILLOW:
      header = 'Poszewki'
      mianownik = 'poszewka'
      biernik = 'poszewkę'
      break
    case Category.TOWEL:
      header = 'Ręczniki'
      mianownik = 'ręcznik'
      biernik = 'ręcznik'
      break
    case Category.TABLE:
      header = 'Nakrycia stołu'
      mianownik = 'nakrycie stołu'
      biernik = 'nakrycie stołu'
      break
  }
  if (capitalize) {
    header = header[0].toUpperCase() + header.slice(1)
    mianownik = mianownik[0].toUpperCase() + mianownik.slice(1)
    biernik = biernik[0].toUpperCase() + biernik.slice(1)
  }
  return { header, biernik, mianownik }
}

export const categoriesForSelect = Object.values(Category).map((value) => ({
  value,
  label: getCategoryName(value, { capitalize: true }).mianownik,
}))

export function getUnitName(unit: Unit) {
  let short: string
  let mianownik: string
  let miejscownik: string
  switch (unit) {
    case Unit.PIECE:
      short = 'szt.'
      mianownik = 'Sztuka'
      miejscownik = 'sztukach'
      break
    case Unit.METER:
      short = 'm'
      mianownik = 'Metr'
      miejscownik = 'metrach'
      break
    case Unit.CENTIMETER:
      short = 'cm'
      mianownik = 'Centymetr'
      miejscownik = 'centymetrach'
      break
  }
  return { short, miejscownik, mianownik }
}

export const orderPlaces = [
  {
    value: OrderPlace.SHIPMENT,
    label: 'Wysyłka',
  },
  {
    value: OrderPlace.WAREHOUSE,
    label: 'Magazyn',
  },
  {
    value: OrderPlace.SHORTAGE,
    label: 'Braki',
  },
  {
    value: OrderPlace.SEWING,
    label: 'Szycie',
  },
  {
    value: OrderPlace.EYELET,
    label: 'Przelotki',
  },
  {
    value: OrderPlace.CUTTING,
    label: 'Docinanie',
  },
  {
    value: OrderPlace.INTERNAL,
    label: 'Firmowe',
  },
  {
    value: OrderPlace.DISPATCHED,
    label: 'Wysłane',
  },
]

export function getOrderPlaceName(place: OrderPlace) {
  const placeName = orderPlaces.find((p) => p.value === place)?.label
  if (!placeName) throw new Error(`Unknown place '${place}'`)
  return placeName
}

export const workTypes = [
  {
    value: WorkType.CUTTING,
    label: 'Docinanie',
  },
  {
    value: WorkType.SEWING,
    label: 'Szycie',
  },
  {
    value: WorkType.SEWING_LOCKSTITCH,
    label: 'Szycie stębnówką',
  },
  {
    value: WorkType.SEWING_DOUBLE_NEEDLE,
    label: 'Szycie dwugłowówką',
  },
  {
    value: WorkType.EYELET,
    label: 'Przelotki',
  },
]

export function getWorkTypeName(workType: WorkType) {
  const workTypeName = workTypes.find((wt) => wt.value === workType)?.label
  if (!workTypeName) throw new Error(`Unknown work type '${workType}'`)
  return workTypeName
}

export function getSewingWorkTypeName(workType: SewingWorkType) {
  // @ts-ignore
  const workTypeName = workTypes.find((wt) => wt.value === workType)?.label
  if (!workTypeName) throw new Error(`Unknown work type '${workType}'`)
  return workTypeName
}

export function getWorkTypeTitle(workType: WorkType) {
  switch (workType) {
    case WorkType.CUTTING:
      return 'Docięte metry'
    case WorkType.SEWING:
      return 'Uszyte metry'
    case WorkType.SEWING_LOCKSTITCH:
      return 'Uszyte metry stębnówką'
    case WorkType.SEWING_DOUBLE_NEEDLE:
      return 'Uszyte metry dwugłowówką'
    case WorkType.EYELET:
      return 'Nabite przelotki'
    default:
      return 'Nieznany typ pracy'
  }
}

export const auditTypes = [
  { value: 'CREATE', label: 'Utworzenie' },
  { value: 'UPDATE', label: 'Aktualizacja' },
  { value: 'DELETE', label: 'Usunięcie' },
  { value: 'SEARCH', label: 'Wyszukiwanie' },
  { value: 'AUTH', label: 'Autoryzacja' },
]

export function getAuditTypeName(type: AuditType) {
  const typeName = auditTypes.find((t) => t.value === type)?.label
  if (!typeName) throw new Error('Unknown audit type')
  return typeName
}

export function getMessageForAuditAction(auditAction: AuditAction): string {
  switch (auditAction) {
    case AuditAction.AUTH_LOGIN:
      return 'Użytkownik zalogował się'
    case AuditAction.AUTH_LOGOUT:
      return 'Użytkownik wylogował się'
    case AuditAction.AUTH_LOGIN_FAIL:
      return 'Nieudana próba logowania'
    case AuditAction.AUTH_PWD_CHANGE:
      return 'Użytkownik zmienił hasło'
    case AuditAction.SEARCH_PRODUCT:
      return 'Wyszukanie produktu'
    case AuditAction.CREATE_VARIANT:
      return 'Dodanie nowego wariantu'
    case AuditAction.CREATE_PRODUCT:
      return 'Dodanie nowego produktu'
    case AuditAction.CREATE_USER:
      return 'Utworzenie nowego użytkownika'
    case AuditAction.UPDATE_PRODUCT:
      return 'Zaktualizowanie produktu'
    case AuditAction.UPDATE_VARIANT:
      return 'Zaktualizowanie wariantu'
    case AuditAction.UPDATE_VARIANT_MOVE:
      return 'Przeniesienie produktu na inną półkę'
    case AuditAction.UPDATE_VARIANT_TAKE:
      return 'Pobranie produktu z magazynu'
    case AuditAction.UPDATE_USER:
      return 'Zaktualizowanie użytkownika'
    case AuditAction.DELETE_PRODUCT:
      return 'Usunięcie produktu'
    case AuditAction.DELETE_VARIANT:
      return 'Usunięcie wariantu'
    case AuditAction.DELETE_USER:
      return 'Usunięcie użytkownika'
    default:
      return 'Nieznana akcja'
  }
}

export const attributesLabels = [
  { value: 'Place', label: 'Miejsce' },
  { value: 'Size', label: 'Wymiar' },
  { value: 'Stock', label: 'Ilość' },
  { value: 'Comments', label: 'Uwagi' },
  { value: 'PillowType', label: 'Rodzaj poszewki' },
  { value: 'AlternateSymbol', label: 'Symbol alternatywny' },
  { value: 'IsAtSale', label: 'Wyprzedaż' },
  { value: 'Category', label: 'Kategoria' },
  { value: 'Unit', label: 'Jednostka' },
  { value: 'Finish', label: 'Wykończenie' },
  { value: 'Feature', label: 'Cecha' },
  { value: 'Image', label: 'Zdjęcie' },
  { value: '', label: '' },
]

export function getAttributeLabel(attribute: string) {
  const attributeLabel = attributesLabels.find((a) => a.value === attribute)?.label
  if (attributeLabel) return attributeLabel
  return attribute
}

export const shortageStatuses = [
  { value: ShortageStatus.NEW, label: 'Nowy' },
  { value: ShortageStatus.ORDERED, label: 'Zamówiony' },
  { value: ShortageStatus.FULFILLED, label: 'Zrealizowany' },
  { value: ShortageStatus.UNFULFILLED, label: 'Niezrealizowany' },
]

export function getShortageStatusLabel(status: ShortageStatus) {
  const statusName = shortageStatuses.find((s) => s.value === status)?.label
  if (!statusName) throw new Error('Unknown shortage status')
  return statusName
}

export function placeFilterFn<T>(row: Row<T>, columnId: string, filterValue: any) {
  const place = row.getValue<Place>(columnId)
  const placeString = `${place.rack}-${place.column}-${place.shelf}`
  return placeString.toLowerCase().includes((filterValue as string).toLowerCase())
}

export const categories = [Category.PREMADE, Category.FABRIC, Category.PILLOW, Category.TOWEL, Category.TABLE]

export function getVariantTable(category: Category) {
  switch (category) {
    case Category.PREMADE:
      return PremadeTable
    case Category.FABRIC:
      return FabricTable
    case Category.PILLOW:
      return PillowTable
    case Category.TOWEL:
      return TowelTable
    case Category.TABLE:
      return TableTable
    default: {
      const _exhaustiveCheck: never = category
      throw new Error(`Unhandled category: ${_exhaustiveCheck}`)
    }
  }
}

export const documentTypes = [
  { value: DocumentType.RECEIPT, label: 'Paragon' },
  { value: DocumentType.INVOICE, label: 'Faktura' },
]

export const paymentMethods = [
  { value: PaymentMethod.PREPAID, label: 'Przedpłata' },
  { value: PaymentMethod.ON_DELIVERY, label: 'Za pobraniem' },
]

export const countries = [
  'Albania',
  'Andora',
  'Armenia',
  'Austria',
  'Azerbejdżan',
  'Białoruś',
  'Belgia',
  'Bośnia i Hercegowina',
  'Bułgaria',
  'Chorwacja',
  'Cypr',
  'Czechy',
  'Dania',
  'Estonia',
  'Finlandia',
  'Francja',
  'Grecja',
  'Hiszpania',
  'Holandia',
  'Irlandia',
  'Islandia',
  'Kanada',
  'Kazachstan',
  'Kosowo',
  'Liechtenstein',
  'Litwa',
  'Luksemburg',
  'Łotwa',
  'Macedonia Północna',
  'Malta',
  'Mołdawia',
  'Monako',
  'Czarnogóra',
  'Niemcy',
  'Norwegia',
  'Polska',
  'Portugalia',
  'Rosja',
  'Rumunia',
  'San Marino',
  'Serbia',
  'Słowacja',
  'Słowenia',
  'Stany Zjednoczone',
  'Szwajcaria',
  'Szwecja',
  'Turcja',
  'Ukraina',
  'Watykan',
  'Węgry',
  'Wielka Brytania',
  'Włochy',
]

export async function getMiniImageUrl(url: string, size: '310' | '400' = '400'): Promise<string> {
  // Input: https://abcfirany.pl/images/zdjecia_produktow/Tkaniny-zaslonowe/modna-tkanina-w-duze-niebieskie-kwiaty-380836-104.jpg
  // Output: https://abcfirany.pl/images/zdjecia_produktow/Tkaniny-zaslonowe/mini/400px_modna-tkanina-w-duze-niebieskie-kwiaty-380836-104.webp
  // If url is already a webp mini version, return it as is
  if (url.includes('/mini/')) return url
  const parts = url.split('/')
  const fileName = parts.pop()
  const fileNameWithoutExt = fileName?.split('.').slice(0, -1).join('.')
  const miniUrl = `${parts.join('/')}/mini/${size}px_${fileNameWithoutExt}.webp`

  return await validateImageUrl(miniUrl).then((isValid) => {
    if (!isValid) {
      return url
    }
    return miniUrl
  })
}

export function validateImageUrl(url?: string | null): Promise<boolean> {
  return new Promise((resolve) => {
    if (!url) return resolve(false)
    const img = new window.Image()
    img.onload = () => resolve(true)
    img.onerror = () => resolve(false)
    img.src = url
  })
}

export function isGuid(value: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidRegex.test(value)
}

export function trimFormValues<T extends Record<string, any>>(values: T, afterTransform?: (trimmedValues: T) => T): T {
  const trimmed = Object.fromEntries(
    Object.entries(values).map(([key, value]) => {
      const isPasswordField = key.toLowerCase().includes('password')

      if (typeof value === 'string' && !isPasswordField) {
        return [key, value.trim()]
      }

      return [key, value]
    })
  ) as T

  return afterTransform ? afterTransform(trimmed) : trimmed
}

export function getShortageOrderStatusText(
  shortageOrder: ShortageOrder | ShortageOrderTableItem,
  long: boolean = false
): string {
  if (shortageOrder.fulfilledAt) {
    if (shortageOrder.fulfilledAmount === -1) {
      return 'Niezrealizowane'
    } else {
      return 'Zrealizowane'
    }
  } else {
    if (shortageOrder.isEmailSent) {
      if (long) {
        return 'Zamówione u producenta, oczekujące na realizację'
      } else {
        return 'Zamówione u producenta'
      }
    } else {
      if (long) {
        return 'Stworzone, oczekujące na zamówienie u producenta'
      } else {
        return 'Stworzone'
      }
    }
  }
}

export function getShortageOrderStatus(shortageOrder: ShortageOrder | ShortageOrderTableItem): ShortageStatus {
  if (shortageOrder.fulfilledAt) {
    if (shortageOrder.fulfilledAmount === -1) {
      return ShortageStatus.UNFULFILLED
    } else {
      return ShortageStatus.FULFILLED
    }
  } else {
    if (shortageOrder.isEmailSent) {
      return ShortageStatus.ORDERED
    } else {
      return ShortageStatus.NEW
    }
  }
}

export function getShortageStatus(shortageOrder: ShortageOrder | ShortageOrderTableItem): ShortageStatus {
  if (shortageOrder.fulfilledAt) {
    if (shortageOrder.fulfilledAmount === -1) {
      return ShortageStatus.UNFULFILLED
    } else {
      return ShortageStatus.FULFILLED
    }
  } else {
    return ShortageStatus.ORDERED
  }
}

export const shortageOrderStatusOptions = [
  { value: ShortageStatus.NEW, label: 'Stworzone' },
  { value: ShortageStatus.ORDERED, label: 'Zamówione u producenta' },
  { value: ShortageStatus.FULFILLED, label: 'Zrealizowane' },
  { value: ShortageStatus.UNFULFILLED, label: 'Niezrealizowane' },
]
